import { AfterViewInit, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ToggleOption } from '../shared/model/ui/toggle-option';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { CmElement, ContentType } from '../shared/model/cm/managed-content.model';
import { AdditionalDetail } from '../shared/model/ui/additional-detail';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { GenericFormComponent } from '../generic-form/generic-form.component';

@Component({
  selector: 'app-button-toggle',
  standalone: true,
  imports: [ReactiveFormsModule, forwardRef(() => GenericFormComponent)],
  templateUrl: './button-toggle.component.html',
  styleUrls: ['./button-toggle.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ButtonToggleComponent implements OnInit, AfterViewInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() toggleOptions!: ToggleOption[];
  @Input() key!: string;
  @Input() label!: string;

  buttonToggleForm!: UntypedFormGroup;
  public controlName!: string;
  public controlLabel!: string;
  public additionalDetailsAnswer!: string;
  public additionalDetails!: AdditionalDetail[];
  
  requiredElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'required-error');
  ongoingResolvedContentError: string = this.requiredElement?.error ?? '';

  public yesNoRadioButtonOptions = [
    { name: 'ongoing', label: 'Ongoing' },
    { name: 'resolved', label: 'Resolved' }
  ];

  constructor(private fb: FormBuilder,
    private cmService: ManagedContentService,
    public formControlUtilitiesService: FormControlUtilitiesService) { }

  ngOnInit(): void {
    if(this.key){
      this.controlName = this.key;
    }

    if(this.label){
      this.controlLabel = this.label;
    }
    
    // Check if parent form already has this control (from restoration)
    if (this.parentForm && this.parentForm.get(this.controlName)) {
      this.buttonToggleForm = this.parentForm.get(this.controlName) as UntypedFormGroup;
    } else {
      // Create new FormGroup
      this.buttonToggleForm = new UntypedFormGroup({ });
      this.buttonToggleForm.addControl(this.controlName, this.fb.control('', [Validators.required]));
    }

    if(this.toggleOptions){
      for(let opt of this.toggleOptions){
        if(opt.additionalDetails){
          this.additionalDetailsAnswer = opt.value;
          this.additionalDetails = opt.additionalDetails;
        }
      }
    }

    this.syncAdditionalDetailsVisibility();
  }

  ngAfterViewInit(): void {
    if (this.parentForm) {
      // Only add control if it doesn't already exist
      if (!this.parentForm.get(this.controlName)) {
        this.parentForm.addControl(this.controlName, this.buttonToggleForm);
      }
    };
  }

  // ngOnDestroy removed - empty lifecycle methods violate linting rules
  // DO NOT remove control from parent form - this causes data loss during navigation
  // The parent form should manage its own controls' lifecycle

  logRadioChangeEvent(key: string, controlName: string){
    this.buttonToggleForm.get(key)?.setValue(controlName);
    this.parentForm.get(key)?.patchValue(controlName);
    this.syncAdditionalDetailsVisibility();
  }

  private syncAdditionalDetailsVisibility(): void {
    if (!this.parentForm || !this.additionalDetails?.length || !this.controlName) {
      return;
    }

    const selectedValue = this.buttonToggleForm?.get(this.controlName)?.value;
    if (selectedValue === this.additionalDetailsAnswer) {
      return;
    }

    this.additionalDetails.forEach(detail => this.removeDetailControls(detail.key, detail.controlType));
  }

  private removeDetailControls(detailKey: string, controlType: string): void {
    const controlsToRemove = [detailKey];

    if (controlType === 'monthPicker') {
      controlsToRemove.push(`${detailKey}-month`, `${detailKey}-year`);
    }

    controlsToRemove.forEach(controlKey => {
      const control = this.parentForm.get(controlKey);
      if (!control) {
        return;
      }

      control.clearValidators();
      control.setErrors(null);
      control.updateValueAndValidity({ emitEvent: false });
      this.parentForm.removeControl(controlKey);
    });
  }
}
